import * as CL from '@design-system/component-library';
import { dsClass } from '../../../common/constants/dsClasses.js';
import {
  billingAccountExtensionNameMsg,
  billingAccountNameMsg,
  cityMsg,
  fieldValueTooLong,
  invalidValueMsg,
  invoiceTypeMsg,
  payerDetailsMsg,
  payerMsg,
  referenceMsg,
  selectMsg,
  streetAddressMsg,
  t,
} from '../../../common/i18n/index.js';
import { Dropdown } from '../../../common/react-hook-form/components/Dropdown.js';
import {
  ContactDropdown,
  Name,
  PostOffice,
  PostalCode,
  Reference,
  StreetAddress,
  TextInput,
} from '../../../common/react-hook-form/fields/index.js';
import {
  billingAccountInvoiceTypes,
  validateBillingAccountReferenceMaxLength,
} from '../../../common/utils/billingAccountUtils.js';
import { NAME_WITH_SPECIAL_CHARACTERS_FORMAT_REGEX } from '../../../common/utils/nameUtils';
import {
  BILLING_ACCOUNT_MAX_LENGTH,
  BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH,
  BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH,
  BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH,
} from '../../../common/utils/validationUtils.js';
import { GridColumn } from '../../BillingAccountDetails/components/GridColumn.js';
import type { CLDropdownItem } from '../../../common/types/componentLibrary';
import type { Contact, ContactHeader } from '../../../generated/api/models.js';

interface RefCallBackProps {
  elementKey: string;
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
}

/**
 * Adds a ref for scroll to error feature
 * Currently this component is part of others that use this approach for scroll to error
 * to be refactored when upper components change
 */
export const RefCallBack = (props: RefCallBackProps) => {
  const { setRefCallback, elementKey } = props;
  if (setRefCallback) {
    return (
      <div
        ref={(element: HTMLElement | null) => {
          setRefCallback(elementKey, element);
        }}
      />
    );
  } else {
    return null;
  }
};

interface BillingAccountFormProps {
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
  contacts?: Contact[] | ContactHeader[];
}

const validateBillingAccountExtensionName = (name: string) => {
  if (!NAME_WITH_SPECIAL_CHARACTERS_FORMAT_REGEX.test(name)) {
    return t.DUPA(invalidValueMsg);
  } else if (name?.length > BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH) {
    return t.XOYE(fieldValueTooLong, `${BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH}`);
  }
  return undefined;
};

const validatePayerNameExtension = (name: string) => {
  if (!NAME_WITH_SPECIAL_CHARACTERS_FORMAT_REGEX.test(name)) {
    return t.DUPA(invalidValueMsg);
  } else if (name?.length > BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH) {
    return t.XOYE(fieldValueTooLong, `${BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH}`);
  }
  return undefined;
};

export const BillingAccountForm = ({ setRefCallback, contacts }: BillingAccountFormProps) => {
  const billingAccountInvoiceTypeOptions: CLDropdownItem[] = billingAccountInvoiceTypes.map(type => ({
    label: type.displayValue(),
    value: type.value,
  }));

  return (
    <>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <TextInput label={t.PB6S(payerMsg)} name="payerName" disabled required />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <TextInput
            label={t.YLAI(payerDetailsMsg)}
            name="payerNameExtension"
            maxLength={BILLING_ACCOUNT_PAYER_NAME_EXTENSION_MAX_LENGTH}
            required={false}
            validate={validatePayerNameExtension}
          />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <RefCallBack elementKey="payerAddress.line1" setRefCallback={setRefCallback} />
          <StreetAddress name="payerAddress.line1" placeholder={t.DD38(streetAddressMsg)} />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <RefCallBack elementKey="payerAddress.postalCode" setRefCallback={setRefCallback} />
          <PostalCode name="payerAddress.postalCode" allowPoBox />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <RefCallBack elementKey="payerAddress.postOffice" setRefCallback={setRefCallback} />
          <PostOffice name="payerAddress.postOffice" placeholder={t.J0YE(cityMsg)} />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field" />
      </CL.GridRow>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field no-max-height">
          <RefCallBack elementKey="billingContactId" setRefCallback={setRefCallback} />
          <ContactDropdown
            canAddNewContacts
            contacts={contacts || []}
            labelText={t.VYZS('Contact person')}
            name="billingContactId"
            placeHolderText={t.QRYV(selectMsg)}
          />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <RefCallBack elementKey="billFormatType" setRefCallback={setRefCallback} />
          <Dropdown
            name="billFormatType"
            id="billFormatType"
            items={billingAccountInvoiceTypeOptions}
            label={t.UW4H(invoiceTypeMsg)}
          />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field" />
        <GridColumn className="of-billing-account-details form-column edit-field">
          <RefCallBack elementKey="billingAccountName" setRefCallback={setRefCallback} />
          <Name
            label={t.RH6T(billingAccountNameMsg)}
            name="billingAccountName"
            placeholder={t.RH6T(billingAccountNameMsg)}
            maxLength={BILLING_ACCOUNT_MAX_LENGTH}
            allowSpecialCharacters
          />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow className={dsClass.PADDING_BOTTOM_8}>
        <GridColumn className="of-billing-account-details form-column edit-field" />
        <GridColumn className="of-billing-account-details form-column edit-field">
          <TextInput
            label={t.KUTS(billingAccountExtensionNameMsg)}
            name="billingAccountExtensionName"
            maxLength={BILLING_ACCOUNT_NAME_EXT_MAX_LENGTH}
            required={false}
            validate={validateBillingAccountExtensionName}
          />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <Reference
            label={`${t.LVRN(referenceMsg)} 1`}
            name="customerReference1"
            required={false}
            maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
            validate={validateBillingAccountReferenceMaxLength}
            hint={undefined}
          />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <Reference
            label={`${t.LVRN(referenceMsg)} 2`}
            name="customerReference2"
            required={false}
            maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
            validate={validateBillingAccountReferenceMaxLength}
            hint={undefined}
          />
        </GridColumn>
      </CL.GridRow>
      <CL.GridRow className={dsClass.PADDING_BOTTOM_8}>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <Reference
            label={`${t.LVRN(referenceMsg)} 3`}
            name="customerReference3"
            required={false}
            maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
            validate={validateBillingAccountReferenceMaxLength}
            hint={undefined}
          />
        </GridColumn>
        <GridColumn className="of-billing-account-details form-column edit-field">
          <Reference
            label={`${t.LVRN(referenceMsg)} 4`}
            name="customerReference4"
            required={false}
            maxLength={BILLING_ACCOUNT_REFERENCE_TEXT_MAX_LENGTH}
            validate={validateBillingAccountReferenceMaxLength}
            hint={undefined}
          />
        </GridColumn>
      </CL.GridRow>
    </>
  );
};
